// src/components/opportunity/OpportunitySalesAdjustmentsSection.tsx
//
// Rep-entered subtotal adjustments (slice 7c). Each line is a $ or %
// delta applied before tax/margin math; description is required when
// the value is non-zero.

import type { OpportunityAmountMode } from "@/types/opportunity";
import {
  makeSalesAdjustment,
  type OpportunityPricingFormAdjustment,
  type OpportunityPricingFormState,
} from "@/components/opportunity/OpportunityPricingForm";

export type OpportunitySalesAdjustmentsSectionProps = {
  form: OpportunityPricingFormState;
  setForm: (next: OpportunityPricingFormState) => void;
};

export function OpportunitySalesAdjustmentsSection(
  props: OpportunitySalesAdjustmentsSectionProps,
) {
  const { form, setForm } = props;

  function updateAdjustment(
    key: string,
    patch: Partial<OpportunityPricingFormAdjustment>,
  ) {
    setForm({
      ...form,
      salesAdjustments: form.salesAdjustments.map((adj) =>
        adj.key === key ? { ...adj, ...patch } : adj,
      ),
    });
  }

  function addAdjustment() {
    setForm({
      ...form,
      salesAdjustments: [...form.salesAdjustments, makeSalesAdjustment()],
    });
  }

  function removeAdjustment(key: string) {
    setForm({
      ...form,
      salesAdjustments: form.salesAdjustments.filter((adj) => adj.key !== key),
    });
  }

  return (
    <section className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-zinc-700 dark:bg-zinc-900">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-xs uppercase tracking-wide text-gray-500 dark:text-zinc-400">
          Subtotal adjustments
        </h4>
        <button
          type="button"
          onClick={addAdjustment}
          className="px-2.5 py-1 rounded-lg border border-gray-300 text-xs text-gray-700 hover:bg-gray-50 dark:border-zinc-700 dark:text-zinc-200 dark:hover:bg-zinc-800"
        >
          + Add adjustment
        </button>
      </div>
      {form.salesAdjustments.length === 0 ? (
        <p className="text-xs text-gray-500 dark:text-zinc-400">
          Optional discounts or surcharges applied to the subtotal before tax.
          Each line needs a description.
        </p>
      ) : (
        <div className="space-y-2">
          {form.salesAdjustments.map((adj) => (
            <div
              key={adj.key}
              className="grid grid-cols-1 sm:grid-cols-[1fr_88px_120px_auto] gap-2 items-end"
            >
              <div>
                <label className="text-xs font-medium text-gray-600 dark:text-zinc-300">
                  Description
                </label>
                <input
                  value={adj.description}
                  onChange={(e) => updateAdjustment(adj.key, { description: e.target.value })}
                  placeholder="e.g. Promotional discount"
                  className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 dark:bg-zinc-800 dark:border-zinc-700 dark:text-zinc-100"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 dark:text-zinc-300">
                  Type
                </label>
                <ModeToggle
                  value={adj.amountMode}
                  onChange={(mode) => updateAdjustment(adj.key, { amountMode: mode })}
                  ariaLabel="Adjustment amount mode"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 dark:text-zinc-300">
                  {adj.amountMode === "percent" ? "Percent" : "Amount"}
                </label>
                <input
                  value={adj.value}
                  onChange={(e) => updateAdjustment(adj.key, { value: e.target.value })}
                  placeholder={adj.amountMode === "percent" ? "0" : "0.00"}
                  inputMode="decimal"
                  className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 tabular-nums dark:bg-zinc-800 dark:border-zinc-700 dark:text-zinc-100"
                />
              </div>
              <button
                type="button"
                onClick={() => removeAdjustment(adj.key)}
                className="px-2 py-2 rounded-lg text-xs text-red-700 hover:bg-red-50 dark:text-red-300 dark:hover:bg-red-950/30"
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function ModeToggle(props: {
  value: OpportunityAmountMode;
  onChange: (next: OpportunityAmountMode) => void;
  ariaLabel: string;
}) {
  return (
    <div
      role="radiogroup"
      aria-label={props.ariaLabel}
      className="mt-1 inline-flex w-full rounded-lg border border-gray-200 overflow-hidden text-xs dark:border-zinc-700"
    >
      {(["amount", "percent"] as const).map((mode) => (
        <button
          key={mode}
          type="button"
          role="radio"
          aria-checked={props.value === mode}
          onClick={() => props.onChange(mode)}
          className={
            "flex-1 px-2 py-2 " +
            (props.value === mode
              ? "bg-indigo-600 text-white"
              : "bg-white text-gray-700 hover:bg-gray-50 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:bg-zinc-800")
          }
        >
          {mode === "amount" ? "$" : "%"}
        </button>
      ))}
    </div>
  );
}
